import { Check } from 'lucide-react';
import RecipeKatIcon from '../receptura/RecipeKatIcon';
import RecipeValidaceBadge from '../receptura/RecipeValidaceBadge';
import RecipeStatusBadges from '../receptura/RecipeStatusBadges';
import { StavBunka } from '../receptura/PrehledBunky';
import { fmtCena, SECTION_COLOR } from '../receptura/receptUtils';
import { GRID_MAT } from './materialGrid';
import { AlergenyBunka, DodavatelBunka, CenaBunka, CenaOdBunka, PouzitoBunka } from './MaterialPrehledBunky';

const VYBER_COLOR = '#3B82F6'; // modrá = označeno pro hromadný přesun

// Ve sloučeném seznamu obalů a spotřebního zboží obarvíme kód — prefix
// OBA-/SPOZ- v něm už je, takže vlastní odznak by říkal totéž dvakrát.
// Kategorie druh neprozradí: při hledání se strom sbalí do plochého seznamu.
const DRUH_BARVA = { OBA: '#10B981', SPOZ: '#EAB308' };

// Řádek položky Spižírny — stejná kostra i chování jako řádek receptáře, ale
// nese jen to, co materiál skutečně má: jednotku, cenu za jednotku, dodavatele,
// alergeny a kolikrát je v kusovnících. Žádné varianty, kalkulace ani porce.
//
// `siroky` = přehled přes celou šířku → řádek sloupcové mřížky (GRID_MAT),
// jinak dvouřádková karta vedle detailu. Stejné rozdělení jako RecipeListRow.
export default function MaterialListRow({
    item, active, selected = false, depthPad = 0, onClick, onDragStartItem,
    isDark, mutedColor, secondaryColor, sAlergeny = false, druh = null,
    siroky = false, sloupce = null,
}) {
    const kodBarva = (druh && DRUH_BARVA[druh]) || mutedColor;
    const cena = fmtCena(item.aktualni_cena);
    const jed = item.jednotka?.zkratka;
    const pouzito = item.kusovniky_jako_ingredience_count;

    const metaBits = [];
    if (item.aktualni_cena_dodavatel) metaBits.push(item.aktualni_cena_dodavatel);
    if (pouzito != null) metaBits.push(`použito ${pouzito}×`);

    const alergeny = item.alergeny || [];

    const podklad = selected
        ? (isDark ? 'rgba(59,130,246,0.16)' : 'rgba(59,130,246,0.10)')
        : active ? (isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.06)') : 'transparent';

    const spolecneProps = {
        'data-list-id': item.id,
        onClick,
        draggable: !!onDragStartItem,
        onDragStart: e => { onDragStartItem?.(e, item); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(item.id)); } catch {} },
        className: `flex items-center cursor-pointer transition-colors ${siroky ? '' : 'gap-2.5'}`,
        style: siroky ? {
            gap: GRID_MAT.mezera,
            padding: '7px 10px',
            paddingLeft: 10 + depthPad,
            borderLeft: `3px solid ${selected ? VYBER_COLOR : (active ? SECTION_COLOR : 'transparent')}`,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            background: podklad,
            opacity: item.aktivni === false ? 0.6 : 1,
            userSelect: 'none',
        } : {
            padding: '8px 10px', paddingLeft: 10 + depthPad,
            borderLeft: selected ? `3px solid ${VYBER_COLOR}` : (active ? `3px solid ${SECTION_COLOR}` : '3px solid transparent'),
            background: podklad,
            opacity: item.aktivni === false ? 0.6 : 1,
            userSelect: 'none', // ať Ctrl+klik označuje řádky, ne text
        },
        onMouseEnter: e => { if (!active && !selected) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)'; },
        onMouseLeave: e => { if (!active && !selected) e.currentTarget.style.background = podklad; },
    };

    const ikona = (
        <span style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
            <RecipeKatIcon ikona={item.kategorie?.ikona} kategorieNazev={item.kategorie?.nazev} kategorieBarva={item.kategorie?.barva}
                size={siroky ? 40 : 36} radius={siroky ? 10 : undefined} />
            {selected && (
                <span style={{
                    position: 'absolute', top: -4, left: -4, width: 16, height: 16, borderRadius: '50%',
                    background: VYBER_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 0 2px ${isDark ? '#1a1a26' : '#fff'}`,
                }}>
                    <Check size={10} strokeWidth={3} style={{ color: '#fff' }} />
                </span>
            )}
        </span>
    );

    // ── Přehled: řádek sloupcové mřížky ──
    if (siroky) {
        // Meta: základní jednotka a v čem se dá ještě počítat (sekundární jednotky).
        const altZkratky = (item.alt_jednotky || []).map(a => a.jednotka_do?.zkratka).filter(Boolean);
        const meta = [jed, altZkratky.length ? `také ${altZkratky.join(', ')}` : null].filter(Boolean);

        return (
            <div {...spolecneProps}>
                <div style={{ width: GRID_MAT.uchyt, flexShrink: 0 }} />
                {sloupce?.nahled !== false && <div style={{ width: GRID_MAT.nahled, flexShrink: 0, display: 'flex' }}>{ikona}</div>}

                <div className="min-w-0" style={{ flex: `${GRID_MAT.polozkaRust} 1 0`, minWidth: GRID_MAT.polozkaMin }}>
                    <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{item.nazev}</span>
                        <span className="text-[10px] font-mono" style={{ color: kodBarva, flexShrink: 0 }}>{item.kod}</span>
                        <RecipeStatusBadges aktivni={item.aktivni} />
                    </div>
                    <div className="text-[11px] truncate" style={{ color: mutedColor, marginTop: 1 }}>
                        {meta.join(' · ') || ' '}
                    </div>
                </div>

                <AlergenyBunka alergeny={alergeny} bezAlergenu={!!item.bez_alergenu} mutedColor={mutedColor} isDark={isDark} />
                {sloupce?.dodavatel !== false && (
                    <DodavatelBunka nazev={item.aktualni_cena_dodavatel} pocet={(item.dodavatele_ids || []).length} mutedColor={mutedColor} />
                )}
                <CenaBunka polozka={item} jednotka={jed} mutedColor={mutedColor} />
                {sloupce?.cenaOd !== false && <CenaOdBunka od={item.aktualni_cena_od} mutedColor={mutedColor} />}
                <PouzitoBunka pocet={pouzito || 0} mutedColor={mutedColor} />
                <StavBunka validace={item.validace} mutedColor={mutedColor} />
            </div>
        );
    }

    // ── Úzký sloupec vedle detailu: dvouřádková karta ──
    return (
        <div {...spolecneProps}>
            {ikona}

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <RecipeValidaceBadge validace={item.validace} size={15} />
                    <span className="text-[13px] font-semibold flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>{item.nazev}</span>
                    <span className="text-[10px] font-mono" style={{ color: kodBarva, flexShrink: 0 }}
                        title={druh ? (druh === 'OBA' ? 'Obal' : 'Spotřební zboží') : undefined}>{item.kod}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] flex-1 min-w-0 truncate" style={{ color: mutedColor }}>
                        {metaBits.join(' · ') || ' '}
                    </span>
                    <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                        {/* Alergeny jen u surovin — obaly ani spotřební zboží je nemají.
                            Neutrálně jako v přehledu: alergen sám o sobě není problém. */}
                        {sAlergeny && alergeny.length > 0 && (
                            <span className="flex items-center gap-0.5">
                                {alergeny.slice(0, 4).map(a => (
                                    <span key={a.id} title={a.nazev} className="inline-flex items-center justify-center rounded-full text-[9px] font-bold"
                                        style={{ width: 16, height: 16, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: 'var(--text-primary)' }}>
                                        {a.cislo_eu}
                                    </span>
                                ))}
                                {alergeny.length > 4 && (
                                    <span className="text-[10px] font-semibold" style={{ color: mutedColor }}>+{alergeny.length - 4}</span>
                                )}
                            </span>
                        )}
                        {sAlergeny && alergeny.length === 0 && item.bez_alergenu && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(34,197,94,0.12)', color: '#22C55E' }}>bez alergenů</span>
                        )}
                        {cena
                            ? <span className="text-[12px] font-bold" style={{ color: 'var(--text-primary)' }}>{cena}{jed ? <span className="font-normal" style={{ color: mutedColor }}>/{jed}</span> : null}</span>
                            : <span className="text-[11px]" style={{ color: mutedColor }} title="Chybí platná cena">—</span>}
                    </div>
                </div>
            </div>
        </div>
    );
}
